import { GoogleMap, InfoBox, Marker } from '@react-google-maps/api';
import { InfoBoxCustomForAction, MapControl } from 'components';
import { CoordinateModel, StageActionItem } from 'models';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { LOCATION_DEFAULT } from 'uniforms';
import { handleGetPlace } from 'utils';
import './MapCustomForAction.scss';

type Props = {
    infoBox?: boolean;
    detailAction?: boolean;
    listAction?: boolean;
    data?: StageActionItem[];
};

const containerStyle = {
    width: '100%',
    height: '100%',
    borderRadius: '8px',
};

const defaultMapOptions = {
    disableDefaultUI: true,
};

const MapCustomForAction = (props: Props): JSX.Element => {
    const { infoBox = false, data = [], detailAction = false, listAction = false } = props;
    const [currentCoordinate, setCurrentCoordinate] = useState<CoordinateModel>(LOCATION_DEFAULT);
    const [map, setMap] = useState<any>(null);
    const [dataStageActionList, setDataStageActionList] = useState<StageActionItem[]>(data);

    /**
     * handle load map
     */
    const handleLoadMap = (map): void => {
        setMap(map);
    };

    /**
     * handle on unmount map
     */
    const handleUnmountMap = (): void => {
        setMap(null);
    };

    /**
     * handle click marker
     */
    const handleClickMarker = useCallback(
        (id: number) => (): void => {
            let list: StageActionItem[] = [...dataStageActionList];
            list.map((item, index) => {
                if (item.action_id === id) {
                    list[index] = { ...item, isShowInfoBox: true };
                    map?.panTo({ lat: item.action_latitude, lng: item.action_longitude });
                } else {
                    list[index] = { ...item, isShowInfoBox: false };
                }
            });
            setDataStageActionList(list);
        },
        [dataStageActionList, map],
    );

    const handleCloseInfoBox = useCallback(
        (id: number) => {
            let list: StageActionItem[] = [...dataStageActionList];
            list.map((item, index) => {
                if (item.action_id === id) {
                    list[index] = { ...item, isShowInfoBox: false };
                }
            });
            setDataStageActionList(list);
        },
        [dataStageActionList],
    );

    /**
     * handle get current location
     */
    const handleGetCurrentLocation = useCallback(async () => {
        if (navigator.geolocation) {
            navigator?.geolocation.getCurrentPosition(async ({ coords: { latitude: lat, longitude: lng } }) => {
                const pos = { lat, lng };
                let des = await handleGetPlace(lat, lng);
                setCurrentCoordinate({ coordinates: pos, description: des });
            });
        }
    }, []);

    useEffect(() => {
        if (!detailAction) {
            handleGetCurrentLocation();
        }
    }, [detailAction]);

    useEffect(() => {
        if (data && data !== dataStageActionList) {
            setDataStageActionList(data);
            if (data.length > 0 && detailAction) {
                setCurrentCoordinate({ coordinates: { lat: data[0].action_latitude, lng: data[0].action_longitude } });
            }
        }
    }, [data]);

    return useMemo(
        () => (
            <GoogleMap
                mapContainerStyle={containerStyle}
                zoom={18}
                center={currentCoordinate.coordinates}
                options={defaultMapOptions}
                onLoad={handleLoadMap}
                onUnmount={handleUnmountMap}>
                <div className="control">
                    {dataStageActionList &&
                        dataStageActionList.length > 0 &&
                        dataStageActionList.map(item => {
                            return (
                                <Marker
                                    key={item.action_id}
                                    position={{
                                        lat: parseFloat(String(item.action_latitude)),
                                        lng: parseFloat(String(item.action_longitude)),
                                    }}
                                    onClick={handleClickMarker(item.action_id)}>
                                    {infoBox && item.isShowInfoBox && (
                                        <InfoBox
                                            position={new google.maps.LatLng(item.action_latitude, item.action_longitude)}
                                            options={{
                                                closeBoxURL: '',
                                                enableEventPropagation: true,
                                                alignBottom: true,
                                                pixelOffset: new google.maps.Size(-130, -35),
                                                zIndex: 20,
                                            }}>
                                            <InfoBoxCustomForAction
                                                data={item}
                                                onClose={(): void => {
                                                    handleCloseInfoBox(item.action_id);
                                                }}
                                            />
                                        </InfoBox>
                                    )}
                                </Marker>
                            );
                        })}
                    <MapControl position="TOP_RIGHT" className="full-screen" listSpot={listAction} />
                </div>
            </GoogleMap>
        ),
        [currentCoordinate.coordinates, dataStageActionList, handleClickMarker, handleCloseInfoBox, infoBox, listAction],
    );
};
export default MapCustomForAction;
